// Shared row behavior for the BatchView data table and the
// BatchItemAlternateStores (cascaded store) data table. Keeping these helpers
// in one place keeps the focal rows and alternate-store rows behaving
// identically and guards against the two tables drifting apart.
export const tprItemRow = {
  computed: {
    srpRules() {
      const rules = []
      const valueRequired = v => !!v || 'SRP Required'
      rules.push(valueRequired)
      const positiveValue = v => parseFloat(v) >= 0 || 'Invalid SRP'
      rules.push(positiveValue)
      return rules
    },
    multiplierRules() {
      const rules = []
      const valueRequired = v => !!v || 'Multiplier Required'
      rules.push(valueRequired)
      const positiveValue = v => v > 0 || 'Invalid Multiplier'
      rules.push(positiveValue)
      return rules
    },
    blockNonCompliantSaves() {
      return this.tprProTenants && !this.isTprProRetailUser
    }
  },
  methods: {
    srpExceedsBaseRetail(item) {
      // AWG does not maintain perishable base retails reliably, so the base-retail ceiling
      // is not trustworthy for perishable batches — exempt them from this gate.
      if (String(this.selectedBatch?.batch_type || '').toUpperCase() === 'PERISHABLE') return false
      const multiplier = Number(item.multiplier) || 1
      if (multiplier <= 0) return false
      const retail = Number(item.retail_price)
      const promo = Number(item.promotion_price)
      if (!(retail > 0) || !(promo > 0)) return false
      return (promo / multiplier) > retail
    },
    srpComplianceLimit(item) {
      const base = this.isTprProRetailUser ? item.original_promotion_price : item.rules_promotion_price
      return (parseFloat(base) + parseFloat(item.variance)).toFixed(2) * item.multiplier
    },
    srpIsCompliant(item) {
      if (item.enforce_compliance == false) return true
      return !(parseFloat(item.promotion_price) > this.srpComplianceLimit(item))
    },
    findMinDiscountRule(item) {
      if (!this.hasRules(item.rules_applied)) return null
      let rules
      try {
        rules = typeof item.rules_applied === 'string' ? JSON.parse(item.rules_applied) : item.rules_applied
      } catch (err) {
        return null
      }
      if (!Array.isArray(rules)) return null
      const rule = rules.find(r => r && r.name && r.name.toLowerCase().includes('discount'))
      if (!rule) return null
      const name = String(rule.name || '').toLowerCase()
      const label = String(rule.label || '').toLowerCase()
      const isPercent = name.includes('pct') || name.includes('percent') || label.includes('percent') || label.includes('%')
      return { rule, isPercent }
    },
    getExclusionReason(item) {
      if (!item) return ''
      const included = item.excluded === '' || item.excluded === null
      if (included) return ''

      // Mirror primary's reason on cascaded rows; generic note otherwise.
      if (item.exclusion_reason) return item.exclusion_reason

      // This is not implemented as a rule, so have to check for seperately outside of below exclusion value check
      if (this.srpExceedsBaseRetail(item)) return 'TPR SRP exceeds Base Retail'

      // When rules are applied, an exclusion string for the exclusion reason is added to the item's data. Use that as a base
      //  to return a more detailed string for the tooltip
      switch (item.excluded) {
        case 'minimum_discount':
          return 'Excluded by Min Discount $ Rule'

        case 'minimum_percentage':
          return 'Excluded by Min Discount % Rule'

        case 'manual':
          return 'Un-included'

        case 'promotion_price':
          return 'Promotion Price is not valid'

        default:
          return item.excluded
      }
    },
    hasRules(rulesApplied) {
      if (!rulesApplied || rulesApplied === '[]') return false
      try {
        const parsed = JSON.parse(rulesApplied)
        return Array.isArray(parsed) && parsed.length > 0
      } catch (err) {
        console.error('Invalid JSON in rules_applied:', err)
        return false
      }
    },
    parseRules(rulesApplied) {
      // Friendly labels for engine marker pseudo-rules.
      const markerLabels = { 'Non-Subscriber': 'Not a Price Pro store' }
      try {
        const parsedRules = typeof rulesApplied === 'string' ? JSON.parse(rulesApplied) : rulesApplied
        return parsedRules.map(rule => {
          if (rule.name && rule.name.toLowerCase().includes('discount')) {
            rule.displayValue = `$${rule.value}`
          } else if (rule.name && rule.name.toLowerCase().includes('pct')) {
            rule.displayValue = `${rule.value}%`
          } else {
            rule.displayValue = rule.value
          }
          rule.displayLabel = markerLabels[rule.name] || rule.display_name || rule.name
          return rule
        })
      } catch (err) {
        console.error('Failed to parse rules_applied:', err)
        return []
      }
    },
    getRuleDisplay(ruleKey) {
      const ruleMapping = {
        'ad_group': 'Ad Group',
        'store': 'Store',
        'department': 'Department',
        'category': 'Category',
        'vendor': 'Vendor'
      }
      return ruleMapping[ruleKey] || ruleKey
    },
    getWarningColor(item) {
      if (this.changes.some(change => change.id === item.id) && this.blockNonCompliantSaves) {
        return 'white'
      }
      return 'red'
    },
    getCompetitorPrice(item, competitor) {
      const value = item[`comp_pricing_${competitor}`]
      return value !== undefined ? value : 'N/A'
    },
    getPriceComparisonColor(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'grey'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'grey'
      if (userprice > compprice) return 'red'
      return 'green'
    },
    getPriceComparisonIcon(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'mdi-minus'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return 'mdi-equal'
      if (userprice > compprice) return 'mdi-arrow-up'
      return 'mdi-arrow-down'
    },
    getPriceComparisonText(item, competitor) {
      const competitorPrice = this.getCompetitorPrice(item, competitor)
      if (!competitorPrice || competitorPrice === 'N/A' || !item.promotion_price) return 'No price comparison available'
      const compprice = parseFloat(competitorPrice)
      const userprice = parseFloat(item.promotion_price) / parseFloat(item.multiplier || 1)
      if (compprice === userprice) return `Prices are equal to $${compprice}`
      if (userprice > compprice) {
        const diff = ((userprice - compprice) / compprice * 100).toFixed(2)
        return `Price ${diff}% More Expensive than Competitor price of $${compprice}`
      }
      const diff = ((compprice - userprice) / compprice * 100).toFixed(2)
      return `Price ${diff}% Cheaper than Competitor price of $${compprice}`
    }
  }
}
